import { Point2D } from '@shared/models/geometry.model';
import { Coordinate } from '@shared/models/geography.model';
import {
  DraftPreviewPayload,
  MapGeometryMeasurementFocus,
  MapHoverState,
  MapHoverStateType,
  MapTooltipPayload,
  MapTooltipType,
  PointPayload,
  ShapeTranslatePayload,
} from '../models';
import { getSignedLineSegmentDragDistance } from '../helpers/point-move-propagation.helpers';

type LocationPinTooltipState = {
  hoverPoint: Point2D | undefined;
  isPinModeActive: boolean;
  canTranslateBuilding: boolean;
  location: Coordinate;
};

export type TooltipComputationInput = {
  pointMove?: PointPayload;
  canDeletePoint: boolean;
  hoverState?: MapHoverState;
  draftPreview?: DraftPreviewPayload;
  hasPointInsertCapability: boolean;
  selectedShapeId?: string;
  shapeTranslationActive?: boolean;
  shapeTranslation?: ShapeTranslatePayload;
  canDragSelectedShapeUnderCursor?: boolean;
  locationPin: LocationPinTooltipState;
  isBottomFloorRestricted?: boolean;
  altKeyActive?: boolean;
  hoverScreenPoint?: Point2D;
};

/** Keep the current label family stable while one measurement input has focus. */
export const resolveGeometryMeasurementFocus = (input: {
  focusedMeasurement?: MapGeometryMeasurementFocus;
  selectedPointCount: number;
  selectedShapeId?: string;
  hoveredPointShapeId?: string;
}): MapGeometryMeasurementFocus | undefined => {
  if (input.focusedMeasurement) return input.focusedMeasurement;
  const angleShapeId =
    input.selectedPointCount > 0
      ? input.selectedShapeId
      : input.hoveredPointShapeId;
  return angleShapeId
    ? { kind: 'corner-angle', shapeId: angleShapeId }
    : undefined;
};

/**
 * Build the active map tooltip(s).
 */
export const computeTooltipPayload = (
  input: TooltipComputationInput,
): MapTooltipPayload[] => {
  const { pointMove, hoverState, draftPreview } = input;
  const shapeTranslation = input.shapeTranslation;

  if (input.locationPin.isPinModeActive) {
    if (input.locationPin.hoverPoint) {
      const coordinates = formatCoordinatePair(input.locationPin.location);
      // Henrik: keep coordinates visible in pin mode to avoid stale length tooltips.
      if (coordinates) {
        return [
          {
            type: MapTooltipType.Action,
            screenPoint: input.locationPin.hoverPoint,
            text: coordinates,
          },
        ];
      }
    }
    return [];
  }

  // Show disabled tooltip when Alt is pressed and bottom floor already has a shape
  if (
    input.isBottomFloorRestricted &&
    input.altKeyActive &&
    input.hoverScreenPoint
  ) {
    return [
      {
        type: MapTooltipType.Action,
        screenPoint: input.hoverScreenPoint,
        text: 'Only one shape allowed on bottom storey',
        disabled: true,
      },
    ];
  }

  if (pointMove?.screenPoint) {
    if (pointMove.isLineSegmentDrag) {
      const draggedDistance = getSignedLineSegmentDragDistance(
        pointMove.shape.points,
        pointMove.index,
        pointMove.offset,
        {
          uniformPolygonOffset: pointMove.uniformPolygonOffset,
          snapIncrementMeters: pointMove.segmentTranslationSnapMeters,
        },
      );
      const signedDistanceText = formatSignedMeters(draggedDistance);
      if (signedDistanceText) {
        return [
          {
            type: MapTooltipType.Distance,
            screenPoint: pointMove.screenPoint,
            text: `${signedDistanceText} m`,
          },
        ];
      }
    }

    if (input.canDeletePoint) {
      return [
        {
          type: MapTooltipType.Action,
          screenPoint: pointMove.screenPoint,
          text: 'Delete point',
        },
      ];
    }
    const distanceText = formatMeters(pointMove.distance);
    if (distanceText) {
      return [
        {
          type: MapTooltipType.Distance,
          screenPoint: pointMove.screenPoint,
          text: `${distanceText} m`,
        },
      ];
    }
  }

  if (shapeTranslation?.screenPoint) {
    const rotationText = formatSignedRotation(
      shapeTranslation.rotation?.radians,
    );
    if (rotationText) {
      return [
        {
          type: MapTooltipType.Distance,
          screenPoint: shapeTranslation.screenPoint,
          text: rotationText,
        },
      ];
    }

    const [dx, dy] = shapeTranslation.delta;
    const distance = Math.hypot(dx ?? 0, dy ?? 0);
    const distanceText = formatMeters(distance);
    if (distanceText) {
      return [
        {
          type: MapTooltipType.Distance,
          screenPoint: shapeTranslation.screenPoint,
          text: `${distanceText} m`,
        },
      ];
    }
  }

  if (
    hoverState?.type === MapHoverStateType.DraftPoint &&
    hoverState.screenPoint &&
    hoverState.canComplete
  ) {
    return [
      {
        type: MapTooltipType.Action,
        screenPoint: hoverState.screenPoint,
        text: 'Finalize shape',
      },
    ];
  }

  const tooltips: MapTooltipPayload[] = [];

  // Tip length/angle only after at least one draft point exists.
  if (
    draftPreview?.screenPoint &&
    (draftPreview.draftPoints?.length ?? 0) >= 1
  ) {
    const distanceText = formatMeters(draftPreview.distance);
    const angleText = formatAngle(
      getDraftPreviewAngle(
        draftPreview.draftPoints,
        draftPreview.snappedPosition,
      ),
    );
    if (distanceText) {
      const details = angleText
        ? `${distanceText} m • ${angleText}`
        : `${distanceText} m`;
      tooltips.push({
        type: MapTooltipType.Distance,
        screenPoint: draftPreview.screenPoint,
        text: details,
      });
    }
  }

  if (tooltips.length) return tooltips;

  if (
    hoverState?.type === MapHoverStateType.DraftPoint &&
    hoverState.screenPoint
  ) {
    return [
      {
        type: MapTooltipType.Action,
        screenPoint: hoverState.screenPoint,
        text: 'Move point',
      },
    ];
  }

  // Committed point hover uses the persistent editable corner-angle labels.
  if (hoverState?.type === MapHoverStateType.Point) return [];

  if (
    input.canDragSelectedShapeUnderCursor &&
    input.hoverScreenPoint &&
    !input.shapeTranslationActive
  ) {
    return [
      {
        type: MapTooltipType.Action,
        screenPoint: input.hoverScreenPoint,
        text: 'Move shape',
      },
    ];
  }

  if (hoverState?.type === MapHoverStateType.Line && hoverState.screenPoint) {
    if (hoverState.insertCandidateActive) {
      return [
        {
          type: MapTooltipType.Action,
          screenPoint: hoverState.screenPoint,
          text: 'Add point',
        },
      ];
    }
  }

  return [];
};
const formatMeters = (value?: number): string | undefined => {
  if (value === undefined || Number.isNaN(value)) return undefined;
  if (value >= 100) return Math.round(value).toString();
  return value.toFixed(1);
};

/** Format an outward/inward displacement with an explicit directional sign. */
const formatSignedMeters = (value?: number): string | undefined => {
  if (value === undefined || !Number.isFinite(value)) return undefined;
  const rounded = Math.round(value * 10) / 10;
  if (rounded > 0) return `+${rounded.toFixed(1)}`;
  if (rounded < 0) return `–${Math.abs(rounded).toFixed(1)}`;
  return '0.0';
};

/** Format a rotation delta in degrees with an explicit directional sign. */
const formatSignedRotation = (radians?: number): string | undefined => {
  if (radians === undefined || !Number.isFinite(radians)) return undefined;
  const degrees = (radians * 180) / Math.PI;
  const rounded = Math.round(degrees * 10) / 10;
  if (rounded > 0) return `+${rounded.toFixed(1)}°`;
  if (rounded < 0) return `–${Math.abs(rounded).toFixed(1)}°`;
  return '0.0°';
};

/**
 * Format a map coordinate pair for tooltip display.
 * @param location {@link Coordinate} to format
 */
const formatCoordinatePair = (location: Coordinate): string | undefined => {
  if (!Number.isFinite(location.lat) || !Number.isFinite(location.lng))
    return undefined;
  return `${location.lat.toFixed(3)}, ${location.lng.toFixed(3)}`;
};

/**
 * Format an angle for tooltip display.
 * @param value Angle in degrees
 */
const formatAngle = (
  value?: number,
  spaceBeforeUnit = false,
): string | undefined => {
  if (value === undefined || Number.isNaN(value)) return undefined;
  const normalized = ((value % 360) + 360) % 360;
  const half = normalized > 180 ? 360 - normalized : normalized;
  return `${Math.round(half)}${spaceBeforeUnit ? ' ' : ''}°`;
};

/**
 * Resolve the angle at the last draft point.
 * @param draftPoints Draft chain points
 * @param snappedPosition Current snapped position
 */
const getDraftPreviewAngle = (
  draftPoints?: Point2D[],
  snappedPosition?: Point2D,
): number | undefined => {
  if (!draftPoints || draftPoints.length < 2 || !snappedPosition) {
    return undefined;
  }

  const lastIndex = draftPoints.length - 1;
  const last = draftPoints[lastIndex];
  const prev = draftPoints[lastIndex - 1];
  if (!last || !prev) return undefined;

  return getCornerAngle(last, prev, snappedPosition);
};

/**
 * Calculate the angle between two vectors around a vertex.
 * @param vertex {@link Point2D} angle vertex
 * @param prev {@link Point2D} previous neighbor
 * @param next {@link Point2D} next neighbor
 */
const getCornerAngle = (
  vertex: Point2D,
  prev: Point2D,
  next: Point2D,
): number | undefined => {
  const v1x = prev[0] - vertex[0];
  const v1y = prev[1] - vertex[1];
  const v2x = next[0] - vertex[0];
  const v2y = next[1] - vertex[1];
  const len1 = Math.hypot(v1x, v1y);
  const len2 = Math.hypot(v2x, v2y);
  if (len1 < 0.001 || len2 < 0.001) return undefined;

  // Clamp dot product to avoid NaNs from floating point drift.
  const dot = (v1x * v2x + v1y * v2y) / (len1 * len2);
  const clamped = Math.max(-1, Math.min(1, dot));
  return (Math.acos(clamped) * 180) / Math.PI;
};
